import React, { useContext, useEffect, useMemo, useRef } from "react";
import classNames from "classnames";
import { getPeriodKey, isNLevelClassReport } from "@common/helpers/reports";
import { CashFlowStatementResponse } from "@common/types/reports/types";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { AccountingReportContext } from "../AccountingReport.context";
import { GlTableCallbacks, GlTableMeta } from "../glDrillDown.lib";
import { hasSingleValueColumn } from "../singleColumnReport";
import { buildCashFlowStatementRows } from "./cashFlowStatementViewBuilder";
import {
    buildCashFlowStatementConfig,
    buildCashFlowStatementView,
} from "./cashFlowStatementViewConfig";
import styles from "./CashFlowStatementTable.module.scss";

interface Props {
    report: CashFlowStatementResponse;
    onDrillDown: GlTableCallbacks["onDrillDown"];
}

/**
 * Cash Flow Statement table rendered via the shared ViewTable runtime.
 * - Columns are built dynamically from report.periods (ephemeral, never persisted)
 * - Total-period columns are pinned right via view.columns[i].options.pin
 * - All sections start expanded (autoExpandOnMount)
 */
export const CashFlowStatementTable: React.FC<Props> = ({
    report,
    onDrillDown,
}) => {
    const viewTableRef = useRef<ViewTableHandle>(null);

    const { numberFormat } = useContext(AccountingReportContext);

    const rows = useMemo(() => buildCashFlowStatementRows(report), [report]);

    const view = useMemo(
        () => buildCashFlowStatementView(report.periods),
        [report.periods],
    );

    const tableMeta: GlTableMeta = useMemo(
        () => ({ callbacks: { onDrillDown } }),
        [onDrillDown],
    );

    const config = useMemo(
        () => buildCashFlowStatementConfig(report.periods, numberFormat),
        [report.periods, numberFormat],
    );

    const isByClassReport = useMemo(
        () => isNLevelClassReport(report.periods),
        [report.periods],
    );

    // Re-mount ViewTable (and reset autoExpandOnMount) when entity or date range changes.
    const reportKey = useMemo(
        () => report.periods.map(getPeriodKey).join("|"),
        [report.periods],
    );

    // Scroll to the rightmost column so the most recent period is visible.
    useEffect(() => {
        const timerId = window.setTimeout(() => {
            viewTableRef.current?.scrollTo({ left: Number.MAX_SAFE_INTEGER });
        }, 0);
        return () => window.clearTimeout(timerId);
    }, [report]);

    return (
        <ViewTable
            key={reportKey}
            ref={viewTableRef}
            data={rows}
            view={view}
            config={config}
            tableMeta={tableMeta}
            className={classNames(styles.cashFlowStatementTable, {
                [styles.byClassReport]: isByClassReport,
            })}
            centerColumns={hasSingleValueColumn(report.periods)}
            rowBorders={false}
            enableColumnResizing
            autoExpandOnMount
            nestingIndentPx={16}
        />
    );
};
